import React from 'react';
import { createContext, useEffect, useState } from 'react';
import useRouter from '../../../utils/useRouter';
import useAction from '../../../utils/useAction';
import { fetchMindItem as fetchMindItemAction } from '../../../actions/mindItems.actions';
import { useSelector } from 'react-redux';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { routes, userType } from '../../../constants';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { selectUser } from '../../../reducers/user.selectors';

function isCourseMindItem(item: MindItem | null): item is MindItem<'course'> {
  return (item as MindItem<'course'>)?.fileType === 'course';
}

export interface CourseCoverPageContextType {
    mindItemId: string;
    isLoading: boolean;
    courseMindItem: Partial<MindItem<'course'>>;
    accessLevel: null | 'none' | 'within-limit' | 'full'
  }
  
export const CourseCoverPageContext = createContext<CourseCoverPageContextType>({
  mindItemId: '',
  isLoading: true,
  courseMindItem: {},
  accessLevel: null,
});

export const CourseCoverPageProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
  const { router, match } = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState<CourseCoverPageContextType['accessLevel']>(null);

  const courseId = match.params.courseId;
  const fetchCourseMindItem = useAction(fetchMindItemAction);
  const mindItem = useSelector((state) => selectMindItem(state, courseId)) || null;
  const courseMindItem: Partial<MindItem<'course'>> = isCourseMindItem(mindItem) ? mindItem  : {};
  const user = useSelector(selectUser);

  useEffect(() => {
    fetchCourseMindItem(courseId);
  }, [courseId, fetchCourseMindItem]);

  useEffect(() => {
    if (courseMindItem.fileType && courseMindItem.fileType !== 'course') {
      router.replace(`/${routes.notFound.root}`);
    }
    if (Object.keys(courseMindItem).length > 0){
      setIsLoading(false);
    }
  }, [courseMindItem, router]);

  useEffect(() => {
    if (!user || !courseMindItem) return;

    switch (user.type) {
      case userType.basis:
        setAccessLevel('none');
        break;
        
      case userType.document: {
        if (
          user.isSchoolUser ||
          user.isLibraryUser ||
          user.isWalkIn
        ){
          setAccessLevel('none');
          break;
        }

        const isInTheLimit = 
          user.didReachCoursesLimit && courseMindItem.course?.registrationStatus !== 'none' || 
          !user.didReachCoursesLimit; 

        setAccessLevel(isInTheLimit ? 'within-limit' : 'none');
        break;
      }
       
      case userType.courses:
        setAccessLevel('full');
        break;

      case userType.kombi:
        setAccessLevel('full');
        break;
    }

  }, [user, courseMindItem]);

  return <CourseCoverPageContext.Provider
    value={{
      mindItemId: courseId,
      isLoading,
      courseMindItem,
      accessLevel,
    }}
  >
    {children}
  </CourseCoverPageContext.Provider>;
};
